import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { API, AuthContext } from '@/App';
import { ShieldAlert, Loader2, Flag, FolderOpen, CheckCircle2 } from 'lucide-react';

const Gap = ({ children, tone = 'amber', testid }) => {
  const cls = tone === 'rose' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800';
  return <span data-testid={testid} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{children}</span>;
};

export default function BackfillReviewDialog({ providers, onClose, onOpenProvider, onChanged }) {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ count: 0, items: [] });
  const [busyId, setBusyId] = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API}/service-providers/qualification/backfill-review`, h)
      .then((r) => setData(r.data || { count: 0, items: [] }))
      .catch(() => toast.error('Could not run the backfill review'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  async function flag(item, resolve) {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/service-providers/${item.id}/qualification/flag-recheck`, { resolve }, h);
      toast.success(resolve ? 'Flag cleared' : `${item.company_name} flagged for re-check`);
      onChanged?.();
      load();
    } catch { toast.error('Could not update the flag'); }
    finally { setBusyId(''); }
  }

  const openCard = (item) => {
    const full = (providers || []).find((p) => p.id === item.id);
    if (full) { onOpenProvider(full); onClose(); }
    else toast.error('Open the provider from the list to review.');
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="backfill-review-dialog">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} className="text-[#C8641E]" />
          <h3 className="text-lg font-bold text-[#0E2A47]">Qualification backfill review</h3>
        </div>
        <p className="text-xs text-slate-500">
          Qualified providers re-checked against current rules (verified W-9, Driver&rsquo;s License,
          NDA/Non-Solicitation, insurance, valid email). QSP numbers and operational status are never changed here &mdash;
          this only surfaces providers qualified under older rules that now fall short.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-slate-500" data-testid="backfill-loading">
            <Loader2 size={16} className="animate-spin" /> Running review&hellip;
          </div>
        ) : data.count === 0 ? (
          <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-4 py-8 text-sm font-semibold text-emerald-700" data-testid="backfill-all-clear">
            <CheckCircle2 size={18} /> All qualified providers meet the current requirements.
          </div>
        ) : (
          <div className="space-y-2" data-testid="backfill-list">
            <p className="text-xs font-semibold text-[#C8641E]">{data.count} qualified provider{data.count > 1 ? 's' : ''} now fall short of current rules.</p>
            {data.items.map((it) => (
              <div key={it.id} className="rounded-lg border border-slate-200 p-3" data-testid={`backfill-item-${it.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {it.qsp_number && <span className="rounded bg-[#0E2A47] px-1.5 py-0.5 text-[10px] font-bold text-white">{it.qsp_number}</span>}
                  <span className="font-semibold text-[#0E2A47]">{it.company_name}</span>
                  {it.qualified_by_exception && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">By exception</span>}
                  {it.recheck?.flagged && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">Flagged</span>}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {it.missing_license && <Gap tone="rose" testid={`backfill-license-${it.id}`}>Driver&rsquo;s License missing/unverified</Gap>}
                  {it.missing_agreement && <Gap tone="rose" testid={`backfill-agreement-${it.id}`}>NDA/Non-Solicitation missing/unverified</Gap>}
                  {it.gaps.missing_docs.filter((d) => d !== 'Driver\u2019s License' && d !== "Driver's License" && d !== 'NDA / Non-Solicitation Agreement').map((d) => <Gap key={d} tone="rose">Missing: {d}</Gap>)}
                  {it.gaps.unverified_docs.filter((d) => d !== "Driver's License" && d !== 'NDA / Non-Solicitation Agreement').map((d) => <Gap key={d}>Unverified: {d}</Gap>)}
                  {it.gaps.expired_docs.map((d) => <Gap key={d} tone="rose">Expired: {d}</Gap>)}
                  {it.gaps.missing_info.map((k) => <Gap key={k}>Info: {k}</Gap>)}
                  {it.gaps.insurance_pending && <Gap>Insurance pending</Gap>}
                  {!it.gaps.email_valid && <Gap tone="rose">Email invalid</Gap>}
                </div>
                {it.recheck?.flagged && (
                  <p className="mt-1 text-[11px] text-slate-500">Flagged by {it.recheck.by_name} · {String(it.recheck.at).slice(0, 10)}{it.recheck.note ? ` — "${it.recheck.note}"` : ''}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => openCard(it)} data-testid={`backfill-open-${it.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                    <FolderOpen size={13} /> Open card
                  </button>
                  {it.recheck?.flagged ? (
                    <button onClick={() => flag(it, true)} disabled={busyId === it.id} data-testid={`backfill-clear-${it.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      {busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Clear flag
                    </button>
                  ) : (
                    <button onClick={() => flag(it, false)} disabled={busyId === it.id} data-testid={`backfill-flag-${it.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-[#C8641E] px-3 py-1.5 text-xs font-semibold text-[#C8641E] hover:bg-[#C8641E]/5 disabled:opacity-50">
                      {busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Flag size={13} />} Flag for re-check
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm" data-testid="backfill-close">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
